import { AnyMemo, Memo } from 'models/Memo';
import { Box } from 'models/Box';
import { Collection } from 'models/Collection';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { QueryKey } from 'core/react-query/queryClient';
import { boxesQuery } from 'networking/mindfeed/queries/boxes';
import { collectionsQuery } from 'networking/mindfeed/queries/collections';
import {
  Destination,
  moveMemo,
} from 'networking/mindfeed/services/memo.service';
import { notify, NotificationType } from 'xstate/actors/NotificationActor';
import { NotificationKind } from 'models/Notification';
import { useOptimisticMemoMutation } from './useOptimisticMemoMutation';
import { useBoxLabels } from 'hooks/useBoxLabels';

type Variables = {
  memo: Memo;
  destinationBox: Box;
  destinationCollection?: Collection;
};

const destinationOf = (variables: Variables): Destination => ({
  boxId: variables.destinationBox.id,
  ...(variables.destinationCollection && {
    collectionId: variables.destinationCollection.id,
  }),
});

export const useMoveMemo = ({
  onSuccess,
}: {
  onSuccess?: (data: AnyMemo) => void;
} = {}) => {
  const { boxName } = useBoxLabels();
  const { selectedSpace, selectedBox, selectedCollection } = useActiveSpace();

  return useOptimisticMemoMutation<AnyMemo, Variables>({
    mutationFn: (variables) =>
      moveMemo(variables.memo.id, destinationOf(variables)),
    optimistic: (cache, variables) => {
      if (cache.contains(variables.memo.id)) {
        cache.update(variables.memo.id, destinationOf(variables));
      }
    },
    onSuccess: (memo, variables, { cache, queryClient }) => {
      const movedToDifferentBox =
        variables.destinationBox.id !== selectedBox?.id;
      // Dropping into another box, or another collection while a collection is
      // in view, removes the memo from the current list; otherwise refresh it.
      const movedOutOfCurrentList =
        movedToDifferentBox ||
        (selectedCollection?.id != null &&
          variables.destinationCollection?.id !== selectedCollection.id);

      if (movedOutOfCurrentList) cache.remove(memo.id);
      else cache.replace(memo);

      // Only the list on screen was edited above. The memo also left or
      // joined other lists in this box (the timeline, the old collection,
      // the new one), which may be cached from an earlier visit; mark them
      // all stale so they reload when next shown instead of hiding the memo
      // until a refresh. The prefix covers every collection of the box.
      queryClient.invalidateQueries({
        queryKey: [QueryKey.MEMOS, selectedBox?.id],
      });

      if (movedToDifferentBox) {
        queryClient.invalidateQueries({
          queryKey: [QueryKey.MEMOS, variables.destinationBox.id],
        });
        queryClient.invalidateQueries({
          queryKey: collectionsQuery(variables.destinationBox.id).queryKey,
        });
        // Both boxes' memo counts changed; moves stay within the space.
        queryClient.invalidateQueries({
          queryKey: boxesQuery(selectedSpace?.id).queryKey,
        });
        // So did both boxes' Journal day counts.
        queryClient.invalidateQueries({
          queryKey: [QueryKey.BOX_ACTIVITY, selectedBox?.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.BOX_ACTIVITY, variables.destinationBox.id],
        });
        // And any media the memo carries moved between their Libraries.
        queryClient.invalidateQueries({
          queryKey: [QueryKey.BOX_MEDIA, selectedBox?.id],
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKey.BOX_MEDIA, variables.destinationBox.id],
        });
      }

      queryClient.invalidateQueries({
        queryKey: collectionsQuery(selectedBox?.id).queryKey,
      });

      const box = boxName(variables.destinationBox.name);
      const destination = variables.destinationCollection
        ? `${box} / ${variables.destinationCollection.name}`
        : box;
      notify(
        NotificationType.MEMO_MOVED,
        NotificationKind.Success,
        true,
        1500,
        {
          destination,
        },
      );
      onSuccess?.(memo);
    },
    onError: () => {
      notify(NotificationType.MEMO_MOVED, NotificationKind.Error);
    },
  });
};
